import { apiFetch, ApiNetworkError } from './apiClient';
import { DiscussionError, type DiscussionFailureKind, type Reference } from './discussions';

/** ADR 0018 D2 (#915): a property the catalog knows, and the units a value may be given in. */
export interface PropertyDefinition {
  readonly id: string;
  readonly name: string;
  readonly synonyms: readonly string[];
  readonly shape: 'QUANTITY';
  readonly dimension: string;
  readonly siUnit: string;
  readonly units: readonly { readonly code: string; readonly symbol: string }[];
  readonly subjectLevel: 'MOLECULE' | 'ISOMER';
  readonly usualConditions: readonly string[];
  /** #931: how close two values must be to agree, in SI -- absolute, or a fraction of the larger. */
  readonly agreement?: { readonly kind: 'ABSOLUTE' | 'RELATIVE'; readonly tolerance: number } | null;
}

/** D3/D7: a value or a range, its UCUM unit and an optional ± uncertainty, all as typed. */
export interface Quantity {
  readonly value: string;
  readonly upper?: string | null;
  readonly unit: string;
  readonly uncertainty?: string | null;
}

/** D5: temperature, pressure or pH (quantities), or a solvent (#920, a Molecule). */
export type ConditionKind = 'temperature' | 'pressure' | 'ph' | 'solvent';

export interface Condition {
  readonly kind: ConditionKind;
  readonly value: string;
  readonly unit: string;
}

/** D6: how the value was obtained. */
export interface Method {
  readonly kind: 'MEASURED' | 'CALCULATED' | 'PREDICTED' | 'REPORTED';
  readonly detail?: string | null;
  readonly confidence?: number | null;
}

/** D8: how far to trust it -- shown, never hidden. */
export type AssertionStanding = 'SOURCED' | 'DATA' | 'UNPUBLISHED' | 'PREDICTED' | 'UNVERIFIED';

export type ReportReason = 'WRONG_VALUE' | 'WRONG_UNIT' | 'WRONG_COMPOUND' | 'OTHER';
export interface AssertionProblemReport {
  readonly id: string;
  readonly reason: ReportReason;
  readonly note: string;
  readonly createdAt: string;
  readonly canDismiss: boolean;
}

export interface Assertion {
  readonly problemReport?: AssertionProblemReport | null;
  readonly id: string;
  readonly propertyId: string;
  readonly value: Quantity;
  readonly siValue: number;
  readonly siUpper: number | null;
  readonly siUnit: string;
  /** A solvent's `value` is its Molecule's id and `label` its name; it has no SI value. */
  readonly conditions: readonly (Condition & {
    readonly siValue: number | null;
    readonly siUnit: string | null;
    readonly label?: string | null;
  })[];
  readonly method: Method;
  readonly references: readonly Reference[];
  readonly unpublished: boolean;
  readonly unpublishedNote: string | null;
  readonly audience: 'PUBLIC' | 'PRIVATE';
  readonly standing: AssertionStanding;
  readonly authorName: string | null;
  readonly mine: boolean;
  readonly supersedes: string | null;
  readonly supersededBy: string | null;
  readonly withdrawn: boolean;
  readonly createdAt: string;
  /** #920 (D14): where an imported value came from; null for a value someone stated here. */
  readonly imported?: ImportedFrom | null;
}

/** #920 (D14): an imported value's origin -- the importer, the contributing source, its exact words. */
export interface ImportedFrom {
  readonly importer: string;
  readonly source: string;
  readonly sourceUrl: string | null;
  /** The text exactly as the source lists it, e.g. "135 °C (rapid heating)". */
  readonly reportedAs: string;
  /** The literature the source cites for it, when it names one. */
  readonly citation: string | null;
  /** When the importer read it. */
  readonly version: string;
}

export interface NewAssertion {
  readonly propertyId: string;
  /** A value; absent for a statement (#947), which sends `text`. */
  readonly value?: Quantity;
  readonly text?: TextValue;
  readonly conditions: readonly Condition[];
  readonly method: Method;
  readonly references: readonly string[];
  readonly unpublished: boolean;
  readonly unpublishedNote?: string | null;
  readonly audience: 'PUBLIC' | 'PRIVATE';
  readonly supersedes?: string | null;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await apiFetch(path, init);
  } catch (error) {
    throw new DiscussionError('unavailable', error instanceof ApiNetworkError ? error.message : 'Network error.');
  }
  if (!response.ok) {
    const kind: DiscussionFailureKind =
      response.status === 404
        ? 'notFound'
        : response.status === 400
          ? 'invalid'
          : response.status === 429
            ? 'rateLimited'
            : response.status === 401
              ? 'sessionExpired'
              : response.status >= 500
                ? 'unavailable'
                : 'unknown';
    const problem = (await response.json().catch(() => undefined)) as { detail?: unknown } | undefined;
    throw new DiscussionError(kind, typeof problem?.detail === 'string' ? problem.detail : `Status ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * ADR 0018 D9 (#920): a property's values summed up for this reader -- how many, their range (in the
 * most-reported unit), and the preferred one, with the step of the stated rule that chose it.
 */
export interface PropertySummary {
  readonly propertyId: string;
  readonly count: number;
  readonly siMin: number;
  readonly siMax: number;
  readonly siUnit: string;
  readonly displayUnit: string;
  readonly displayMin: number | null;
  readonly displayMax: number | null;
  readonly preferredId: string | null;
  readonly reasons: readonly ('ONLY_ELIGIBLE_VALUE' | 'ONLY_VALUE' | 'BEST_EVIDENCE' | 'BEST_METHOD' | 'MOST_RECENT')[];
}

export function getPropertySummaries(moleculeId: string): Promise<readonly PropertySummary[]> {
  return call<readonly PropertySummary[]>(`/molecules/${encodeURIComponent(moleculeId)}/assertions/summary`);
}

/** #920 (D5): the common solvents the form offers in one tap; picking one links its Molecule. */
export interface CommonSolvent {
  readonly key: string;
  readonly name: string;
  readonly smiles: string;
}

export function getSolvents(): Promise<readonly CommonSolvent[]> {
  return call<readonly CommonSolvent[]>('/solvents');
}

/**
 * #947 (ADR 0018 D3 shape 7): a statement property -- a description, a use, a toxicity summary. No
 * unit dimension, no units, no SI value.
 */
export interface StatementProperty extends Omit<PropertyDefinition, 'shape' | 'dimension' | 'siUnit'> {
  readonly shape: 'TEXT' | 'CATEGORY';
  readonly dimension: null;
  readonly siUnit: null;
}

/** #947: what the catalog lists -- quantities and statements. */
export type CatalogEntry = PropertyDefinition | StatementProperty;

/** #947: a statement's text, with its language (BCP 47). */
export interface TextValue {
  readonly text: string;
  readonly language: string;
}

/** #947: a statement as an assertion -- the same record as a value, its `text` in place of a quantity. */
export interface StatementAssertion extends Omit<Assertion, 'value' | 'siValue' | 'siUnit'> {
  readonly value: null;
  readonly siValue: null;
  readonly siUnit: null;
  readonly text: TextValue;
}

export const isStatementProperty = (entry: CatalogEntry): entry is StatementProperty => entry.shape !== 'QUANTITY';
export const isStatementAssertion = (assertion: Assertion | StatementAssertion): assertion is StatementAssertion =>
  'text' in assertion && assertion.text != null;

export function getProperties(): Promise<readonly CatalogEntry[]> {
  return call<readonly CatalogEntry[]>('/properties');
}

export function getMoleculeAssertions(moleculeId: string): Promise<readonly (Assertion | StatementAssertion)[]> {
  return call<readonly (Assertion | StatementAssertion)[]>(`/molecules/${encodeURIComponent(moleculeId)}/assertions`);
}

export function createAssertion(moleculeId: string, assertion: NewAssertion): Promise<Assertion> {
  return call<Assertion>(`/molecules/${encodeURIComponent(moleculeId)}/assertions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(assertion)
  });
}

export function withdrawAssertion(assertionId: string): Promise<void> {
  return call<void>(`/assertions/${encodeURIComponent(assertionId)}/withdraw`, { method: 'POST' });
}

export function reportAssertion(
  id: string,
  report: { readonly reason: ReportReason; readonly note: string }
): Promise<void> {
  return call(`/assertions/${encodeURIComponent(id)}/reports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(report)
  });
}
export function dismissAssertionReport(id: string, reportId: string): Promise<void> {
  return call(`/assertions/${encodeURIComponent(id)}/reports/${encodeURIComponent(reportId)}/dismiss`, {
    method: 'POST'
  });
}
